import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { empty, errorResponse, isArray, prepareResponseData } from "../../Utilities/utils";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { AuthContext } from "../Root/ProtectedRoute";

// css
import "../students/Students.css";

// api
import classApi from "../../api/Classes";
import subjectApi from "../../api/Subject";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import TableLoading from "../../components/skeleton/TableLoading";
import colors from "../../config/colors";

const SubjectsToAssess = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  // ref
  const toastTR = useRef(null);
  // states
  const [subjectData, setSubjectData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [classSubjects, setClassSubjects] = useState([]);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [subjectIds, setSubjectIds] = useState([]);
  const [baseNoOfSubjects, setBaseNoOfSubjects] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [classTitle, setClassTitle] = useState("");
  const [headerChecked, setHeaderChecked] = useState(false);
  const [showAddButton, setShowAddButton] = useState(false);
  const [selectedSubjectData, setSelectedSubjectData] = useState({});

  const paramState = !empty(location.state) ? location.state : {};
  const classId =
    !empty(paramState) && !empty(paramState.classId) ? paramState.classId : "";
  const sessionId =
    !empty(paramState) && !empty(paramState.sessionId)
      ? paramState.sessionId
      : "";
  const termId =
    !empty(paramState) && !empty(paramState.termId) ? paramState.termId : "";

  /**
   * Response dialog initialization
   */
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    if (empty(classId) || empty(termId) || empty(sessionId)) {
      return navigate("/404");
    }
    setIsLoading(true); // Set loading true before fetching
    getClassDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, navigate, classId, sessionId, termId]);

  useEffect(() => {
    setShowAddButton(selectedSubjectIds.length > 0);
    setBaseNoOfSubjects(selectedSubjectIds.length || classSubjects.length || 0);
    setHeaderChecked(
      subjectIds.length > 0 &&
        selectedSubjectIds.length === subjectIds.length &&
        selectedSubjectIds.every((id) => subjectIds.includes(id))
    );
  }, [selectedSubjectIds, classSubjects, subjectIds]);

  useEffect(() => {
    checkBoxBodyTemplate(selectedSubjectData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubjectData]);

  /**
   * Get subjects (updated to include debugging)
   */
  const getSubjects = async (subjectsInClass) => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getSubjectsInClass(
        classId,
        schoolId,
        10000
      );
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        setSubjectData([]);
        setSubjectIds([]);
        return;
      }
      const results =
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : [];
      const updatedSubjectIds = results
        .map((item) => item._id)
        .filter((id) => !empty(id));
      const updatedResults = results.map((item) => ({
        ...item,
        added: subjectsInClass.includes(item._id),
      }));
      setSubjectIds(updatedSubjectIds);
      setSubjectData(updatedResults);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get Class Details (updated to include debugging and ensure sync)
   */
  const getClassDetails = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getSingleClass(classId, schoolId);
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.response) ||
        !response_data.success
      ) {
        responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Failed to fetch class details"
        );
        setSubjectData([]);
        setSubjectIds([]);
        setSelectedSubjectIds([]);
        setIsLoading(false);
        return;
      }
      const data = response_data.response || {};
      const classTitle = !empty(data.title) ? data.title : "";
      const reports = !empty(data.reports) ? data.reports : [];
      let currentReportSubjectIds = [];
      if (!empty(reports)) {
        for (let i = 0; i < reports.length; i++) {
          const itemTermId =
            !empty(reports[i]) && !empty(reports[i].termId)
              ? reports[i].termId
              : "";
          const itemSessionId =
            !empty(reports[i]) && !empty(reports[i].sessionId)
              ? reports[i].sessionId
              : "";
          if (itemTermId === termId && itemSessionId === sessionId) {
            setActiveIndex(reports[i]?.noOfSubjectsToGrade || 0);
            currentReportSubjectIds = !empty(reports[i])
              ? reports[i].subjectsToAssess || []
              : [];
            break;
          }
        }
      }
      const subjectsInClass = !empty(data.subjects) ? data.subjects : [];
      setSelectedSubjectIds(currentReportSubjectIds);
      setClassSubjects(subjectsInClass);
      setClassTitle(classTitle);
      await getSubjects(subjectsInClass);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
      setIsLoading(false);
    }
  };

  /**
   * position class with selected subjects
   */
  const positionClass = async () => {
    try {
      if (!isActionLoading) setIsActionLoading(true);
      let _dataToUpdate = [];
      if (isArray(classSubjects) && isArray(selectedSubjectIds)) {
        _dataToUpdate = [...new Set([...classSubjects, ...selectedSubjectIds])];
      }
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.positionClass(
        schoolId,
        classId,
        sessionId,
        termId,
        _dataToUpdate,
        activeIndex
      );
      const response_data = prepareResponseData(response);
      setSelectedSubjectIds([]);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        responseDialog(
          "error",
          "Something went wrong",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Operation failed"
        );
      } else {
        responseDialog("success", "Success", "Class positioned successfully");
        getClassDetails();
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  /**
   * Position a single subject
   */
  const positionSubjects = async (subjectId) => {
    try {
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await subjectApi.positionSubject(
        schoolId,
        classId,
        sessionId,
        termId,
        subjectId === "multiple" ? selectedSubjectIds : [subjectId]
      );
      const response_data = prepareResponseData(response);
      setSelectedSubjectIds([]);
      if (empty(response_data) || !response_data.success) {
        responseDialog(
          "error",
          "Something went wrong",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Operation failed"
        );
      } else {
        responseDialog("success", "Success", "Subject positioned successfully");
        getClassDetails();
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  /**
   * Handle row checkbox toggle
   */
  const handleRowCheckboxChange = (subjectId) => {
    setSelectedSubjectIds((prev) => {
      const newIds = prev.includes(subjectId)
        ? prev.filter((id) => id !== subjectId)
        : [...prev, subjectId];
      return newIds;
    });
  };

  /**
   * Checkbox body template
   */
  const checkBoxBodyTemplate = (rowData) => {
    // Guard against undefined or invalid rowData
    if (!rowData || empty(rowData?._id)) {
      return <Checkbox checked={false} disabled />;
    }
    const subjectId = rowData._id;
    const isChecked = selectedSubjectIds.includes(subjectId);

    return (
      <Checkbox
        checked={isChecked}
        onChange={(e) => {
          setSelectedSubjectData(rowData);
          handleRowCheckboxChange(subjectId);
        }}
      />
    );
  };

  /**
   * Status template
   */
  const statusTemplateBody = (rowData) => {
    if (!rowData || empty(rowData?._id)) {
      return <Tag className="mr-2" icon="pi pi-times" severity="danger" />;
    }
    const subjectId = rowData._id;
    const included = selectedSubjectIds.includes(subjectId);
    return (
      <Tag
        className="mr-2"
        icon={included ? "pi pi-check" : "pi pi-times"}
        severity={included ? "success" : "danger"}
      />
    );
  };

  /**
   * Action template
   */
  const actionTemplate = (rowData) => {
    if (!rowData || empty(rowData?._id)) {
      return null;
    }
    const subjectId = rowData._id;
    return (
      <ButtonIcon
        borderColor={colors.primary}
        backgroundColor={colors.primary}
        color={colors.white}
        buttonText="Position Subject"
        marginRight={1}
        width={140}
        borderHover={false}
        height={30}
        onClick={() => positionSubjects(subjectId)}
      />
    );
  };

  // Update the DataTable rendering in the return statement
  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="tableCard">
            <MainHeader
              children={
                showAddButton && (
                  <>
                    <ButtonIcon
                      borderColor={colors.primary}
                      backgroundColor={colors.primary}
                      color={colors.white}
                      buttonText="Position Class"
                      marginRight={1}
                      width={170}
                      borderHover={false}
                      height={30}
                      onClick={() => positionClass()}
                    />

                    <ButtonIcon
                      borderColor={colors.primary}
                      backgroundColor={colors.primary}
                      color={colors.white}
                      buttonText="Position Subjects"
                      marginRight={1}
                      width={170}
                      borderHover={false}
                      height={30}
                      onClick={() => positionSubjects("multiple")}
                    />
                  </>
                )
              }
              redirect={false}
              title={
                <>
                  <span>Add/Remove subject(s) to Assess in </span>
                  <strong>
                      { classTitle &&
                        <NavLink to={`/classes/${classId}/profile`} style={{ color: colors.primary, textDecoration: 'none' }}>{classTitle}</NavLink>
                      }
                  </strong>
                </>
              }
            />

            <div style={{ marginRight: 40 }}>
              <strong>Select Base Number: </strong>
              <br />
              <div className="mt-10">
                <em>
                  <strong className="text-danger">Note: </strong> This is the
                  number of subjects you want to base your average on. For
                  instance, if the class offers 14 subjects but each student is
                  expected to only offer 9 subject, you would have to select 9
                  here, because it is the base number.
                </em>
              </div>
            </div>
            <div className="flex row-g-12 col-g-12 flex-wrap mb-20 mt-10">
              {Array.from({ length: baseNoOfSubjects }, (_, index) => (
                <div
                  key={index + 1}
                  className="subject-assessment-box"
                  style={{
                    color:
                      activeIndex === index + 1 ? colors.white : colors.black,
                    backgroundColor:
                      activeIndex === index + 1 ? colors.primary : colors.white,
                  }}
                  onClick={() => setActiveIndex(index + 1)}
                >
                  {index + 1}
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 10 }}>
              <span style={{ marginRight: 40 }}>
                <strong>No. of Subjects In class: </strong>
                {classSubjects.length}
              </span>
              <span>
                <strong>No. of Selected Subjects: </strong>
                {selectedSubjectIds.length}
              </span>
            </div>
            <div style={{ marginBottom: 10 }}>
              <span style={{ color: "red", fontStyle: "italic" }}>
                Add subjects that you intend the class positioning to be
                performed with.
              </span>
            </div>
            {!isLoading ? (
              subjectData.length > 0 ? (
                <DataTable
                  value={subjectData}
                  stateKey="dt-state-subjects-to-assess"
                  tableStyle={{ minWidth: "30rem" }}
                  key={selectedSubjectIds.join("-")}
                >
                  <Column
                    field="_id"
                    header={
                      <Checkbox
                        checked={headerChecked}
                        onChange={(e) => {
                          setHeaderChecked(e.checked);
                          setSelectedSubjectIds(
                            e.checked ? [...subjectIds] : []
                          );
                          setShowAddButton(e.checked && subjectIds.length > 0);
                        }}
                      />
                    }
                    body={checkBoxBodyTemplate}
                  />
                  <Column field="title" header="Title" />
                  <Column field="code" header="Code" />
                  <Column
                    field="status"
                    header="Status"
                    body={statusTemplateBody}
                  />
                  <Column
                    field="action"
                    header="Action"
                    body={actionTemplate}
                  />
                </DataTable>
              ) : (
                <p>No subjects available.</p>
              )
            ) : (
              <TableLoading cols={4} rows={20} header={false} />
            )}
          </div>
        </main>
        {isActionLoading && <FullPageLoader visible={isActionLoading} />}
      </AppWrapper>

      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default SubjectsToAssess;
